// /src/pages/AboutPage.jsx
import React from 'react';
import { Shield, Server, GraduationCap, Users, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cardCls, headingCls, bodyText, mutedText, ctaPrimary, pageHeading, sectionLabel, pageWrap, sectionInner } from '../components/styles';

const milestones = [
  {
    icon: Users,
    title: 'Corrections & Training',
    description:
      'Started in corrections as an officer and training supervisor. Learned how systems break under pressure, how people circumvent controls, and why policy without enforcement is theater. That operational mindset never left.',
  },
  {
    icon: Server,
    title: 'IRS Network & Systems Admin',
    description:
      'Contracted as a network and systems administrator for the IRS. Managed federal infrastructure: Active Directory, Group Policy, endpoint security, network segmentation. Defending government networks teaches you what real compliance looks like versus checkbox compliance.',
  },
  {
    icon: GraduationCap,
    title: 'Champlain College',
    description:
      'BS in Cybersecurity (3.88 GPA), Graduate Certificate in Blockchain, and an MBA in Healthcare Administration. Champlain\'s hands-on program meant building real systems, not just studying theory.',
  },
  {
    icon: Shield,
    title: 'DarkHorse Information Security',
    description:
      'Founded DarkHorse to bring builder-grade security to healthcare, education, and government. Not a consultancy that hands you a scanner report, but a firm that builds the tools it uses and deploys the platforms it recommends.',
  },
];

export default function AboutPage() {
  return (
    <div className={pageWrap}>
      {/* Hero */}
      <section className="py-12" style={{ background: 'var(--bg)' }}>
        <div className={sectionInner}>
          <div className="max-w-6xl animate-stagger">
            <h1 className={`${pageHeading} mb-4`}><span className="opacity-50">$</span> about</h1>
            <p className={`text-xl ${mutedText} max-w-3xl leading-relaxed`}>
              Most cybersecurity consultants audit systems. I build them. HADES is a 153k-line metadata
              forensics engine deployed via Kubernetes. VitalChain is a Hyperledger Fabric blockchain platform
              piloting with Vermont EMS on AWS. I didn't outsource either of them.
            </p>
          </div>
        </div>
      </section>

      <hr className="section-rule" />

      {/* Journey */}
      <section className="py-12" style={{ background: 'var(--bg2)' }}>
        <div className={sectionInner}>
          <div className="max-w-6xl">
            <h2 className={`${sectionLabel} mb-6`}>{'// journey'}</h2>
            <div className="space-y-6 animate-stagger">
              {milestones.map(({ icon: Icon, title, description }, i) => (
                <div key={i} className={`${cardCls} p-8 flex gap-6 accent-border-left`}>
                  <div className="flex-shrink-0">
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center"
                      style={{ background: 'rgba(10,191,218,0.12)' }}
                    >
                      <Icon className="text-[var(--accent)]" size={24} />
                    </div>
                  </div>
                  <div>
                    <h3 className={`text-xl ${headingCls} mb-2`}>{title}</h3>
                    <p className={`${bodyText} leading-relaxed`}>{description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <hr className="section-rule" />

      {/* What I'm Building */}
      <section className="py-12" style={{ background: 'var(--bg)' }}>
        <div className={sectionInner}>
          <div className="max-w-6xl">
            <h2 className={`${sectionLabel} mb-6`}>{'// current work'}</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Link to="/tools" className="glow-card block rounded-lg border border-[color:var(--border)] bg-[var(--card)] p-8 hover:translate-y-[-2px] transition">
                <h3 className={`text-2xl ${headingCls} mb-3`}>HADES</h3>
                <p className={`${mutedText} mb-4`}>
                  Metadata forensics engine. YARA + ML detection, MITRE ATT&CK mapping,
                  evidence chains, playbook automation. Production v1.0.0.
                </p>
                <span className="text-[var(--accent)] font-semibold inline-flex items-center gap-1">
                  Learn more <ArrowRight size={16} />
                </span>
              </Link>
              <Link to="/tools" className="glow-card block rounded-lg border border-[color:var(--border)] bg-[var(--card)] p-8 hover:translate-y-[-2px] transition">
                <h3 className={`text-2xl ${headingCls} mb-3`}>VitalChain Health</h3>
                <p className={`${mutedText} mb-4`}>
                  Blockchain healthcare platform. Emergency records + Medicaid eligibility on Hyperledger
                  Fabric. Piloting on AWS with Vermont EMS.
                </p>
                <span className="text-[var(--accent)] font-semibold inline-flex items-center gap-1">
                  Learn more <ArrowRight size={16} />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <hr className="section-rule" />

      {/* Credentials */}
      <section className="py-12" style={{ background: 'var(--bg2)' }}>
        <div className={sectionInner}>
          <div className="max-w-6xl">
            <h2 className={`${sectionLabel} mb-6`}>{'// credentials'}</h2>
            <div
              className="rounded-lg p-10"
              style={{
                background: 'linear-gradient(135deg, rgba(10,191,218,0.08), rgba(193,232,111,0.08))',
                border: '1px solid var(--border)',
              }}
            >
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className={`font-bold ${headingCls} mb-3`}>Education</h4>
                  <ul className={`space-y-2 ${bodyText}`}>
                    <li>MBA, Healthcare Administration - Champlain College (2026)</li>
                    <li>Graduate Certificate, Blockchain - Champlain College</li>
                    <li>BS, Cybersecurity - Champlain College (3.88 GPA)</li>
                  </ul>
                </div>
                <div>
                  <h4 className={`font-bold ${headingCls} mb-3`}>Experience</h4>
                  <ul className={`space-y-2 ${bodyText}`}>
                    <li>Founder - DarkHorse Information Security</li>
                    <li>Founder - VitalChain Health</li>
                    <li>Network & Systems Admin - IRS (contractor)</li>
                    <li>Training Supervisor - Corrections</li>
                  </ul>
                </div>
              </div>

              {/* Capability portfolio. Deliberately linked from here and nowhere
                  else: not in the nav, not in the sitemap, and the page itself
                  is noindex. See PORTFOLIO-SYNC.md.

                  Must be a plain <a>, not a react-router <Link>. The target is
                  static HTML served from public/, so client-side routing would
                  match the "*" route and render the 404 page instead. */}
              <div
                className="mt-8 pt-6"
                style={{ borderTop: '1px solid var(--border)' }}
              >
                <h4 className={`font-bold ${headingCls} mb-2`}>Work samples</h4>
                <p className={`${bodyText} mb-3`}>
                  Recorded security assessment demonstrations, a sample finding written
                  the way it would be delivered, and case studies covering AI safety
                  boundaries, verification engineering, build pipeline supply chain, and
                  malware detection.
                </p>
                <a
                  href="/capability-portfolio/"
                  className={`${ctaPrimary} text-sm`}
                >
                  Capability Portfolio
                  <ArrowRight size={16} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr className="section-rule" />

      {/* CTA */}
      <section className="py-12" style={{ background: 'var(--bg)' }}>
        <div className={sectionInner}>
          <div className="max-w-6xl space-y-4">
            <p className={`text-xl ${mutedText}`}>
              Want to work together?
            </p>
            <Link to="/contact" className={ctaPrimary}>
              Book a consultation <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
